import { useCallback } from "react";

import type { PriceRankItem } from "@/lib/gql/__generated__/graphql";

import type { ProductRoomPriceRankFormModel } from "../../../../../model/price-rank-and-daily-status/_shared/price-rank/type";
import type { ProductRoomPriceRankOnSetViewModelType } from "../../../../../model/price-rank-and-daily-status/_shared/price-rank/use-case/use-room-price-rank-model";

type Props = {
	roomPriceRankMast: ProductRoomPriceRankFormModel["ViewModel"];
	onSetRoomPriceRankMast: ProductRoomPriceRankOnSetViewModelType;

	onSetSelectedPriceRank: (input: PriceRankItem) => void;
};

type RankItemType = ProductRoomPriceRankFormModel["ViewModel"]["ranks"]["value"][0];

export const useRoomPriceRankListItem = ({
	roomPriceRankMast,
	onSetRoomPriceRankMast,
	onSetSelectedPriceRank: _onSetSelectedPriceRank, //
}: Props) => {
	const onSetRoomPriceRankItem = useCallback(
		(index: number) => (value: RankItemType) => {
			const newItems = roomPriceRankMast.ranks.value.map((rank, idx) => {
				if (idx === index) {
					return value;
				} else {
					return rank;
				}
			});

			onSetRoomPriceRankMast([
				{
					key: "ranks",
					value: {
						isEdited: true,
						hasError: false,
						value: newItems,
					},
				},
			]);
		},
		[onSetRoomPriceRankMast, roomPriceRankMast.ranks],
	);

	const onSetPriceRankColor = useCallback(
		(idx: number, priceRankItem: RankItemType) => (color: string) => {
			const newPriceRankItem = {
				...priceRankItem,
				color: {
					isEdited: true,
					value: color,
				},
			} satisfies RankItemType;

			onSetRoomPriceRankItem(idx)(newPriceRankItem);
		},
		[onSetRoomPriceRankItem],
	);

	const onSetPriceRankName = useCallback(
		(idx: number, priceRankItem: RankItemType) => (name: string) => {
			const newPriceRankItem = {
				...priceRankItem,
				name: {
					isEdited: true,
					value: name,
				},
			} satisfies RankItemType;

			onSetRoomPriceRankItem(idx)(newPriceRankItem);
		},
		[onSetRoomPriceRankItem],
	);

	const onSetIsStopSelling = useCallback(
		(idx: number, priceRankItem: RankItemType) => (isStopSelling: boolean | undefined) => {
			const newPriceRankItem = {
				...priceRankItem,
				isStopSelling: {
					isEdited: true,
					value: !isStopSelling,
				},
			} satisfies RankItemType;

			onSetRoomPriceRankItem(idx)(newPriceRankItem);
		},
		[onSetRoomPriceRankItem],
	);

	return {
		onSetPriceRankColor,
		onSetPriceRankName,
		onSetIsStopSelling,
	};
};
